import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Tooltip, Popconfirm, Card, Statistic, Row, Col } from "antd";
import {
  Button,
  Form,
  Input,
  Modal,
  Table,
  Typography,
  notification,
  Select,
  Tag,
} from "antd";
import ajaxService from "../../../services/ajax-service";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const { Text, Title } = Typography;

// Simple Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error in LegalPages:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong in Legal Pages. Please refresh.</h1>;
    }
    return this.props.children;
  }
}

// Modal Content Component (isolates Form and CKEditor to avoid warnings)
const LegalPageModal = ({ 
  visible, 
  onCancel, 
  isViewMode, 
  isEditMode, 
  editingPageId, 
  viewingPage, 
  typeOptions, 
  onFinish, 
  modalWidth,
  onUpdate 
}) => {
  const [form] = Form.useForm();

  // Set form values when viewing/editing data loads
  useEffect(() => {
    if (visible && (isViewMode || isEditMode) && viewingPage) {
      form.setFieldsValue({
        title: viewingPage.title,
        description: viewingPage.description,
        state_id: viewingPage.state_id,
        type_id: viewingPage.type_id,
      });
    } else if (visible && !isViewMode && !isEditMode) {
      form.resetFields();
    }
  }, [visible, isViewMode, isEditMode, viewingPage, form]);

  const renderDescriptionField = () => {
    if (isViewMode) {
      const description = viewingPage?.description || '';
      return (
        <div
          dangerouslySetInnerHTML={{ __html: description }}
          style={{ 
            color: 'var(--text-white)', 
            lineHeight: 1.6,
            border: '1px solid var(--border-color)',
            padding: 'var(--spacing-sm)',
            borderRadius: 'var(--radius-sm)',
            minHeight: '120px',
            maxHeight: '300px',
            overflow: 'auto',
            background: 'var(--dark-bg-hover)'
          }}
        />
      );
    } else {
      return (
        <div 
          className="ck-editor-wrapper" 
          style={{ 
            border: '1px solid var(--border-color)', 
            borderRadius: 'var(--radius-sm)', 
            minHeight: '120px',
            maxHeight: '300px',
            overflow: 'auto',
            background: 'var(--dark-bg-hover)'
          }}
        >
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (
              <CKEditor
                key="ckeditor-editor" // Ensure stable key for remounting
                editor={ClassicEditor}
                data={getFieldValue('description') || ''}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  form.setFieldsValue({ description: data });
                }}
                config={{
                  toolbar: [
                    'heading', '|',
                    'bold', 'italic', 'underline', 'strikethrough', '|',
                    'link', '|',
                    'bulletedList', 'numberedList', '|',
                    'outdent', 'indent', '|',
                    'blockQuote', 'insertTable', '|',
                    'undo', 'redo'
                  ],
                  // Removed fixed height to allow auto-sizing up to wrapper maxHeight
                }}
                disableWatchdog={true} // Disable watchdog to avoid constructor errors on older versions
              />
            )}
          </Form.Item>
        </div>
      );
    }
  };

  const handleFinish = useCallback((values) => {
    onFinish(values, isEditMode, editingPageId, form);
  }, [onFinish, isEditMode, editingPageId, form]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  return (
    <Modal
      className="modal-overlay"
      width={modalWidth}
      open={visible}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      centered
      afterOpenChange={onUpdate}
    >
      <div style={{ 
        padding: "var(--spacing-2xl) var(--spacing-xl)",
        background: 'var(--dark-bg-card)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <div style={{ 
          marginBottom: 'var(--spacing-2xl)', 
          textAlign: "center",
          paddingBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
        }}>
          <Title id="modal-title" level={3} style={{ 
            color: 'var(--text-white)', 
            margin: 0,
            fontFamily: 'Poppins-Bold',
            fontSize: '1.5rem'
          }}>
            {isViewMode ? `View Page` : (isEditMode ? "Edit Page" : "Add New Legal Page")}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Title</span>}
                name="title"
                rules={[{ required: true, message: "Title is required" }]}
              >
                <Input
                  placeholder="Enter title"
                  allowClear
                  className="input-field"
                  disabled={isViewMode}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Description</span>}
                name="description"
                rules={[{ required: true, message: "Description is required" }]}
              >
                {renderDescriptionField()}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Type</span>}
                name="type_id"
                rules={[{ required: true, message: "Type is required" }]}
              >
                <Select
                  placeholder="Select type"
                  className="input-field"
                  disabled={isViewMode}
                  allowClear
                >
                  {typeOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>State</span>}
                name="state_id"
                rules={[{ required: true, message: "State is required" }]}
              >
                <Select
                  placeholder="Select state"
                  className="input-field"
                  disabled={isViewMode}
                >
                  <Select.Option value={1}>Active</Select.Option>
                  <Select.Option value={0}>Inactive</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ 
            textAlign: "center", 
            marginTop: 'var(--spacing-2xl)',
            paddingTop: 'var(--spacing-lg)',
            borderTop: '1px solid rgba(254, 2, 125, 0.2)',
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              key="cancel"
              onClick={handleCancel}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                key="finish"
                type="primary"
                htmlType="submit"
                className="btn-primary"
                style={{ 
                  minWidth: '120px',
                  height: '40px'
                }}
              >
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            )}
          </div>
        </Form>
      </div>
    </Modal>
  );
};

const LegalPages = () => {
  const [showModal, setShowModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewingPage, setViewingPage] = useState(null);
  const [editingPageId, setEditingPageId] = useState(null);

  const [dataSource, setDataSource] = useState({ data: [], current_page: 1, per_page: 10, total: 0 });

  const [searchTerm, setSearchTerm] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const debounceTimer = useRef(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modalWidth, setModalWidth] = useState('50%');

  const pageSize = 10;

  const typeOptions = [
    { label: 'Terms and Conditions', value: 'term' },
    { label: 'Privacy Policy', value: 'privacy' },
    { label: 'User License Agreement', value: 'user_license_agreement' },
  ];

  const getTypeLabel = useCallback((typeId) => {
    const option = typeOptions.find(opt => opt.value === typeId);
    return option ? option.label : typeId || 'N/A';
  }, []);

  const getStatusText = useCallback((stateId) => {
    switch (parseInt(stateId)) {
      case 1: return 'Active';
      default: return 'Inactive';
    }
  }, []);

  const getStatusColor = useCallback((stateId) => {
    switch (parseInt(stateId)) {
      case 1: return 'green';
      default: return 'default';
    }
  }, []);

  const stripHtmlTags = useCallback((html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').substring(0, 50) + (html.length > 50 ? '...' : '');
  }, []);

  const fetchPages = useCallback(async (page = 1, search = '', status = 'all', type = '') => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) {
        params.append('name', search.trim());
      }
      if (status !== 'all') {
        params.append('active', status === '1' ? '1' : '0');
      }
      if (type) {
        params.append('type_id', type);
      }
      params.append('page', page.toString());
      params.append('per_page', pageSize.toString());

      const queryString = params.toString();

      const response = await ajaxService.get(`/pages?${queryString}`);
      if (response.success) {
        setDataSource({
          data: response.data.data || [],
          current_page: response.data.current_page || page,
          per_page: response.data.per_page || pageSize,
          total: response.data.total || 0
        });
      } else {
        setDataSource({ data: [], current_page: 1, per_page: pageSize, total: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch pages:", error);
      setDataSource({ data: [], current_page: 1, per_page: pageSize, total: 0 });
    }
  }, [pageSize]);

  const openViewModal = useCallback((record) => {
    setIsViewMode(true);
    setIsEditMode(false);
    setViewingPage(record);
    setEditingPageId(null);
    setShowModal(true);
  }, []);

  const openCreateModal = useCallback(() => {
    setIsViewMode(false);
    setIsEditMode(false);
    setViewingPage(null);
    setEditingPageId(null);
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((record) => {
    setIsViewMode(false);
    setIsEditMode(true);
    setViewingPage(record);
    setEditingPageId(record.id);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    try {
      const response = await ajaxService.delete(`/pages/`,id);
      if (response.success) {
        fetchPages(dataSource.current_page, searchTerm, statusFilter, typeFilter);
        notification.open({
          message: response.message || "Page deleted successfully!",
          icon: <CheckCircleOutlined style={{ color: "var(--primary-purple)" }} />,
        });
      } else {
        notification.open({
          message: response.message || "Unable to delete page.",
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    } catch (error) {
      console.error("Error deleting page:", error);
      notification.open({
        message: "Unable to delete page.",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  }, [dataSource.current_page, searchTerm, statusFilter, typeFilter, fetchPages]);

  const handleFinish = useCallback(async (values, isEdit, pageId, formInstance) => {
    try {
      const payload = {
        title: values.title,
        description: values.description,
        state_id: parseInt(values.state_id),
        type_id: values.type_id,
      };

      let response;
      if (isEdit && pageId) {
        response = await ajaxService.put(`/pages/`,pageId, payload);
      } else {
        response = await ajaxService.post("/pages", payload);
      }

      if (response.success) {
        handleCancel();
        fetchPages(dataSource.current_page, searchTerm, statusFilter, typeFilter);
        notification.open({
          message: response.message || (isEdit ? "Page updated successfully!" : "Page created successfully!"),
          icon: <CheckCircleOutlined style={{ color: "var(--primary-purple)" }} />,
        });
      } else {
        notification.open({
          message: response.message || "Unable to save page.",
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    } catch (error) {
      console.error("Error saving page:", error);
      notification.open({
        message: "Unable to save page.",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  }, [dataSource.current_page, searchTerm, statusFilter, typeFilter, fetchPages]);

  const handleSearch = useCallback((search) => {
    const query = search ? search.trim() : '';
    setSearchTerm(query);
    fetchPages(1, query, statusFilter, typeFilter);
  }, [statusFilter, typeFilter, fetchPages]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      handleSearch(searchValue);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue, handleSearch]);

  const handleStatusFilterChange = useCallback((value) => {
    setStatusFilter(value);
    fetchPages(1, searchTerm, value, typeFilter);
  }, [searchTerm, typeFilter, fetchPages]);

  const handleTypeFilterChange = useCallback((value) => {
    setTypeFilter(value);
    fetchPages(1, searchTerm, statusFilter, value);
  }, [searchTerm, statusFilter, fetchPages]);

  const handleChangePage = useCallback((page) => {
    fetchPages(page, searchTerm, statusFilter, typeFilter);
  }, [searchTerm, statusFilter, typeFilter, fetchPages]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setIsViewMode(false);
    setIsEditMode(false);
    setViewingPage(null);
    setEditingPageId(null);
  }, []);

  useEffect(() => {
    fetchPages(1, '', statusFilter, typeFilter);
  }, [fetchPages, statusFilter, typeFilter]);

  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 1000, 1350, 40);
  }, [sidebarCollapsed]);

  const [tableScrollX, setTableScrollX] = useState(() => calculateTableScrollX(false, 1000, 1350, 40));

  useEffect(() => {
    const handleResize = () => {
      setTableScrollX(calculateTableWidth());
      setModalWidth(calculateModalWidth('50%'));
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateTableWidth]);

  const handleModalUpdate = useCallback((open) => {
    if (!open) {
      handleCancel();
    }
  }, [handleCancel]);

  const columns = useMemo(() => [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 80,
      fixed: 'left',
      render: (text, record, index) => (
        (dataSource.current_page - 1) * dataSource.per_page + index + 1
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 250,
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (title) => <Text>{title}</Text>,
    },
    {
      title: "Description",
      key: "description",
      width: 300,
      ellipsis: true,
      render: (_, record) => <Text>{stripHtmlTags(record.description)}</Text>,
    },
    {
      title: "Type",
      dataIndex: "type_id",
      key: "type_id",
      width: 150,
      render: (typeId) => <Text>{getTypeLabel(typeId)}</Text>,
    },
    {
      title: "Status",
      key: "state_id",
      width: 120,
      sorter: (a, b) => (a.state_id || 0) - (b.state_id || 0),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => (
        <Tag color={getStatusColor(record.state_id)}>
          {getStatusText(record.state_id)}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      sortDirections: ['ascend', 'descend'],
      render: (createdAt) => <Text>{createdAt ? new Date(createdAt).toLocaleDateString() : 'N/A'}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                color: 'var(--primary-purple)',
                border: '1px solid var(--primary-purple)',
                borderRadius: 'var(--radius-md)',
                transition: 'var(--transition-fast)'
              }}
              icon={<EyeOutlined style={{ fontSize: '12px' }} />}
              onClick={() => openViewModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                color: 'var(--accent-purple)',
                border: '1px solid var(--accent-purple)',
                borderRadius: 'var(--radius-md)',
                transition: 'var(--transition-fast)'
              }}
              icon={<EditOutlined style={{ fontSize: '12px' }} />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Page"
            description="Are you sure you want to delete this page?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--error-red)',
                  border: '1px solid var(--error-red)',
                  borderRadius: 'var(--radius-md)',
                  transition: 'var(--transition-fast)'
                }}
                icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ], [dataSource.current_page, dataSource.per_page, getTypeLabel, getStatusColor, getStatusText, stripHtmlTags, openViewModal, openEditModal, handleDelete]);

  return (
    <ErrorBoundary>
      <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Header Section */}
        <div className="module-header">
          <h1 className="module-title">Legal Pages Management</h1>
          <p className="module-subtitle">Manage legal and informational pages</p>
        </div>

        {/* Statistics Cards */}
        {/* <Row gutter={16} style={{ marginBottom: 'var(--spacing-lg)' }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Pages"
                value={dataSource.total || 0}
                prefix={<EyeOutlined />}
                valueStyle={{ color: 'var(--primary-purple)' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Active Pages"
                value={dataSource.data?.filter(p => p.state_id === 1).length || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: 'var(--success-green)' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Inactive Pages"
                value={dataSource.data?.filter(p => p.state_id !== 1).length || 0}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: 'var(--error-red)' }}
              />
            </Card>
          </Col>
        </Row> */}

        {/* Filters and Search */}
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-item">
              <Input
                placeholder="Search pages by title..."
                prefix={<SearchOutlined style={{ color: 'var(--text-tertiary)' }} />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={(e) => {
                  setSearchValue(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="input-field"
                allowClear
              />
            </div>
            <div className="filter-item">
              <Select
                placeholder="Filter by status"
                style={{ width: '100%' }}
                className="input-field"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <Select.Option value="all">All Status</Select.Option>
                <Select.Option value="1">Active</Select.Option>
                <Select.Option value="0">Inactive</Select.Option>
              </Select>
            </div>
            <div className="filter-item">
              <Select
                placeholder="Filter by type"
                style={{ width: '100%' }}
                className="input-field"
                value={typeFilter}
                onChange={handleTypeFilterChange}
                allowClear
              >
                <Select.Option value="">All Types</Select.Option>
                {typeOptions.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                ))}
              </Select>
            </div>
            <div className="filter-actions">
              <Tooltip title="Create a new legal page">
                <Button
                  className="btn-primary"
                  icon={<EditOutlined />}
                  onClick={openCreateModal}
                  style={{ flex: 1 }}
                >
                  New Page
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Pages Table */}
        <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Table
            columns={columns}
            dataSource={dataSource.data || []}
            className="data-table"
            rowKey="id"
            pagination={{
              onChange: handleChangePage,
              current: dataSource.current_page,
              pageSize: dataSource.per_page,
              total: dataSource.total,
              className: "data-table-pagination",
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} pages`,
              size: 'small'
            }}
            scroll={{ x: tableScrollX }}
            size="small"
            bordered={false}
          />
        </div>

        <LegalPageModal
          visible={showModal}
          onCancel={handleCancel}
          isViewMode={isViewMode}
          isEditMode={isEditMode}
          editingPageId={editingPageId}
          viewingPage={viewingPage}
          typeOptions={typeOptions}
          onFinish={handleFinish}
          modalWidth={modalWidth}
          onUpdate={handleModalUpdate}
        />
      </div>
    </ErrorBoundary>
  );
};

export default LegalPages;